import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface TitresSecondaryDTO {
  id?: string;
  isin: string;
  typeInstrument: string;
  dueDate: string;
  rate: number;
  unitNominalValue: number;
  emissionId?: string;
  amortizationMode?: string;
  outstandingAmount?: number;
  averageMaturity?: number;
  sourceFile?: string;
  deleted?: boolean;
  enabled?: boolean;
}

export interface PageResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
  };
  totalElements: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class TitresSecondaryService {
  private http = inject(HttpClient);
  private readonly apiUrl = 'income/v1/titres/secondary';

  /**
   * Get all secondary titres with pagination
   */
  getAllTitres(page: number = 0, size: number = 10): Observable<PageResponse<TitresSecondaryDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<PageResponse<TitresSecondaryDTO>>(this.apiUrl, { params });
  }

  /**
   * Get titre by ID
   */
  getTitreById(id: string): Observable<TitresSecondaryDTO> {
    return this.http.get<TitresSecondaryDTO>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get titre by ISIN
   */
  getTitreByIsin(isin: string): Observable<TitresSecondaryDTO> {
    return this.http.get<TitresSecondaryDTO>(`${this.apiUrl}/isin/${isin}`);
  }

  /**
   * Create a new titre
   */
  createTitre(titre: TitresSecondaryDTO): Observable<TitresSecondaryDTO> {
    return this.http.post<TitresSecondaryDTO>(this.apiUrl, titre);
  }

  /**
   * Update an existing titre
   */
  updateTitre(id: string, titre: TitresSecondaryDTO): Observable<TitresSecondaryDTO> {
    return this.http.put<TitresSecondaryDTO>(`${this.apiUrl}/${id}`, titre);
  }

  /**
   * Delete a titre (soft delete)
   */
  deleteTitre(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Import titres from Excel file
   */
  importFromExcel(file: File): Observable<TitresSecondaryDTO[]> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<TitresSecondaryDTO[]>(`${this.apiUrl}/import/excel`, formData);
  }

  /**
   * Get titres by source file
   */
  getTitresBySourceFile(sourceFile: string, page: number = 0, size: number = 10): Observable<PageResponse<TitresSecondaryDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<PageResponse<TitresSecondaryDTO>>(`${this.apiUrl}/source-file/${sourceFile}`, { params });
  }

  /**
   * Delete titres by source file
   */
  deleteBySourceFile(sourceFile: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/source-file/${sourceFile}`);
  }
}
